/** Submit step — summary and celebration. */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleXmark,
  faSparkles,
  faSpinner,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { AceComparisonPanel } from "./AceComparisonPanel";
import { ReviewVersionHistory } from "./ReviewVersionHistory";
import type {
  AceProfile,
  AgentRun,
  ArtefactApprovalDecision,
  ArtefactRatings,
  PhaseRatings,
  Rag,
  ReviewComparison,
  ReviewCriteria,
  ReviewPhase,
} from "./types";
import { ragOption } from "./RagOption";
import { artefactRatingsComplete } from "./ArtefactsPhase";
import { hasPartialArtefactRatings } from "./reviewPersistence";
import { ArtefactApprovalBadge, ArtefactApprovalSelector } from "./ArtefactApprovalSelector";

import type { WizardStep } from "./wizardSteps";

import { artefactRowLabel, artefactsAceSummaryHint, artefactsUnsavedHint } from "./artefactReviewCopy";

interface SummaryScreenProps {
  run: AgentRun;
  criteria: ReviewCriteria;
  ratings: PhaseRatings;
  artefactRatings: ArtefactRatings;
  onEdit: (step: Extract<WizardStep, "orchestrate" | "output" | "artefacts">) => void;
  onCompareArtefact?: (artifactId: string) => void;
  onArtefactApprovalChange?: (artifactId: string, approval: ArtefactApprovalDecision) => void;
  submitted: boolean;
  /** Already submitted — show summary recap, not the post-submit celebration. */
  reviewComplete?: boolean;
  needsSave?: boolean;
  canSavePartialArtefacts?: boolean;
  showAceRatings?: boolean;
  showAcePanel?: boolean;
  aceComparison?: ReviewComparison | null;
  aceLoading?: boolean;
  aceError?: string | null;
  onRunAce?: () => void;
  onRerunAce?: () => void;
  reviewId?: string | null;
  currentVersion?: number;
  versionRefreshToken?: number;
  showVersionHistory?: boolean;
  catalogueKey?: string | null;
  aceTrialRefreshToken?: number;
  onRunFreshAce?: (args: {
    criteriaVersionId: string | null;
    aceProfile: AceProfile;
    note: string;
    promote: boolean;
  }) => void;
  onSelectAceTrial?: (trialId: string) => void;
  onPromoteAceTrial?: (trialId: string) => void;
  focusCheckId?: string | null;
}

const PHASES: {
  key: ReviewPhase;
  step: Extract<WizardStep, "orchestrate" | "output">;
  label: string;
}[] = [
  { key: "orchestration", step: "orchestrate", label: "Orchestrate" },
  { key: "output", step: "output", label: "Output" },
];

function aceRagForPhase(comparison: ReviewComparison | null | undefined, phase: ReviewPhase): Rag | null {
  const ace = comparison?.ace_rating;
  if (!ace) return null;
  return phase === "orchestration" ? ace.orchestration.suggested_rag : ace.output.suggested_rag;
}

function aceRagForArtefact(
  comparison: ReviewComparison | null | undefined,
  artifactId: string,
): Rag | null {
  return (
    comparison?.ace_rating?.artefacts?.find((r) => r.artifact_id === artifactId)?.suggested_rag ??
    null
  );
}

function aceApprovalForArtefact(
  comparison: ReviewComparison | null | undefined,
  artifactId: string,
) {
  return (
    comparison?.ace_rating?.artefacts?.find((r) => r.artifact_id === artifactId)
      ?.recommended_approval ?? null
  );
}

function SummaryRagBadge({
  rag,
  label,
  loading = false,
}: {
  rag: Rag | null;
  label: string;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-indigo-300">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[10px]" aria-hidden />
        <span className="text-slate-400">{label}:</span>
        Rating…
      </span>
    );
  }
  if (!rag) {
    return <span className="text-xs text-slate-500">{label}: —</span>;
  }
  const opt = ragOption(rag);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${opt.text}`}>
      <span className="text-slate-500 font-normal">{label}</span>
      <span className={`w-1.5 h-1.5 rounded-full ${opt.dot}`} aria-hidden />
      {opt.label}
    </span>
  );
}

export function SummaryScreen({
  run,
  criteria,
  ratings,
  artefactRatings,
  onEdit,
  onCompareArtefact,
  onArtefactApprovalChange,
  submitted,
  reviewComplete = false,
  needsSave = false,
  canSavePartialArtefacts = false,
  showAceRatings = false,
  showAcePanel = false,
  aceComparison = null,
  aceLoading = false,
  aceError = null,
  onRunAce,
  onRerunAce,
  reviewId = null,
  currentVersion,
  versionRefreshToken = 0,
  showVersionHistory = false,
  catalogueKey = null,
  aceTrialRefreshToken = 0,
  onRunFreshAce,
  onSelectAceTrial,
  onPromoteAceTrial,
  focusCheckId = null,
}: SummaryScreenProps) {
  const hasArtefacts = (run.artefacts?.length ?? 0) > 0;
  const artefacts = run.artefacts ?? [];
  const artefactCriteria = run.artefact_review_criteria;
  const phasesRated = ratings.orchestration.human_rag !== null && ratings.output.human_rag !== null;
  const artefactsRated = artefactRatingsComplete(run, artefactRatings);
  const partialArtefacts = hasPartialArtefactRatings(run, artefactRatings);
  const canSave =
    phasesRated &&
    (artefactsRated || !hasArtefacts || (canSavePartialArtefacts && partialArtefacts));
  const reviewSaved = (submitted || reviewComplete) && !needsSave;
  const aceReviewInProgress = showAceRatings && aceLoading;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      {needsSave && reviewComplete ? (
        <div className="flex items-start gap-3 rounded-xl bg-amber-500/[0.08] px-4 py-3 ring-1 ring-amber-500/20">
          <p className="text-sm text-amber-100">
            {hasArtefacts
              ? artefactsUnsavedHint(artefacts, canSave, run.catalogue_key)
              : "You have unsaved changes. Click Save changes when you are finished."}
          </p>
        </div>
      ) : null}

      {submitted && !reviewComplete ? (
        <div className="flex items-start gap-3 rounded-xl bg-emerald-500/[0.08] px-4 py-3 ring-1 ring-emerald-500/20">
          <FontAwesomeIcon icon={faSparkles} className="mt-0.5 text-emerald-300" />
          <div>
            <p className="text-sm font-medium text-emerald-100">Review submitted</p>
            <p className="text-xs text-emerald-200/80 mt-0.5">
              {aceReviewInProgress
                ? hasArtefacts
                  ? `Ace is rating this run — cheat sheet scores will appear below.`
                  : "Ace is rating this run — scores will appear below."
                : "Thanks — your ratings help us train and improve the agent."}
            </p>
          </div>
        </div>
      ) : null}

      {aceReviewInProgress ? (
        <div
          className="flex items-start gap-3 rounded-xl bg-indigo-500/[0.08] px-4 py-3 ring-1 ring-indigo-500/20"
          data-testid="ace-review-loading-banner"
        >
          <FontAwesomeIcon icon={faSpinner} className="mt-0.5 animate-spin text-indigo-300" />
          <div>
            <p className="text-sm font-medium text-indigo-100">Ace is reviewing this case</p>
            <p className="mt-0.5 text-xs text-indigo-200/80">
              {hasArtefacts
                ? `Checking orchestration, output, and ${artefacts.length} ${artefactRowLabel(artefacts[0], run.catalogue_key).toLowerCase()}${artefacts.length === 1 ? "" : "s"}. This can take a few minutes.`
                : "Checking orchestration and output."}
            </p>
          </div>
        </div>
      ) : null}

      <div>
        <h2 className="text-base font-medium text-slate-900 dark:text-slate-100">
          {reviewSaved ? "Your review" : "Check your ratings"}
        </h2>
        <p className="text-sm text-slate-400 mt-1">
          {aceReviewInProgress
            ? "Your ratings are saved. Ace's scores will fill in below when ready."
            : reviewSaved
              ? showAceRatings && hasArtefacts
                ? artefactsAceSummaryHint(artefacts, run.catalogue_key)
                : "You already submitted this case. Edit any step if something needs updating."
              : "Edit anything that looks wrong, then submit."}
        </p>
      </div>

      <div
        className={`space-y-2 transition-opacity duration-200 ${aceReviewInProgress ? "opacity-90" : ""}`}
      >
        {PHASES.map(({ key, step, label }) => {
          const rating = ratings[key];
          const humanRag = rating.human_rag;
          const aceRag = showAceRatings ? aceRagForPhase(aceComparison, key) : null;
          const disagrees = Boolean(humanRag && aceRag && humanRag !== aceRag);
          return (
            <div
              key={key}
              className="flex min-w-0 items-start gap-4 rounded-xl bg-white/[0.02] px-4 py-3 ring-1 ring-white/5"
            >
              <span className="w-24 flex-shrink-0 pt-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                {label}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-200">{criteria[key].question}</p>
                {rating.human_note ? (
                  <p className="mt-1 text-xs text-slate-400 italic">&ldquo;{rating.human_note}&rdquo;</p>
                ) : null}
              </div>
              <div className="flex w-44 flex-shrink-0 flex-col items-end gap-2">
                <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                  {showAceRatings ? (
                    <>
                      <SummaryRagBadge rag={humanRag} label="You" />
                      <SummaryRagBadge
                        rag={aceRag}
                        label="Ace"
                        loading={aceLoading && !aceComparison?.ace_rating}
                      />
                      {humanRag && aceRag ? (
                        disagrees ? (
                          <FontAwesomeIcon icon={faCircleXmark} className="text-amber-400 text-xs" />
                        ) : (
                          <FontAwesomeIcon icon={faCircleCheck} className="text-emerald-400 text-xs" />
                        )
                      ) : null}
                    </>
                  ) : humanRag ? (
                    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${ragOption(humanRag).text}`}>
                      <span className={`w-2 h-2 rounded-full ${ragOption(humanRag).dot}`} aria-hidden />
                      {ragOption(humanRag).label}
                    </span>
                  ) : (
                    <span className="text-sm text-rose-400">Not rated</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onEdit(step)}
                  className="text-xs text-indigo-300 hover:text-indigo-200"
                >
                  Edit
                </button>
              </div>
            </div>
          );
        })}
        {hasArtefacts && artefactCriteria
          ? (run.artefacts ?? []).map((artefact) => {
              const rating = artefactRatings[artefact.artifact_id];
              const humanRag = rating?.human_rag ?? null;
              const humanApproval = rating?.human_approval ?? null;
              const aceRag = showAceRatings
                ? aceRagForArtefact(aceComparison, artefact.artifact_id)
                : null;
              const aceApproval = showAceRatings
                ? aceApprovalForArtefact(aceComparison, artefact.artifact_id)
                : null;
              const disagrees = Boolean(humanRag && aceRag && humanRag !== aceRag);
              const approvalDisagrees = Boolean(
                humanApproval && aceApproval && humanApproval !== aceApproval,
              );
              return (
                <div
                  key={artefact.artifact_id}
                  className="flex min-w-0 items-start gap-4 rounded-xl bg-white/[0.02] px-4 py-3 ring-1 ring-white/5"
                >
                  <span className="w-24 flex-shrink-0 pt-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                    {artefactRowLabel(artefact, run.catalogue_key)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-slate-200" title={artefact.title}>
                      {artefact.title}
                    </p>
                    {rating?.human_note ? (
                      <p className="mt-1 line-clamp-2 text-xs italic text-slate-400">
                        &ldquo;{rating.human_note}&rdquo;
                      </p>
                    ) : null}
                    {onArtefactApprovalChange ? (
                      <div className="mt-3">
                        <ArtefactApprovalSelector
                          value={humanApproval}
                          onChange={(value) => onArtefactApprovalChange(artefact.artifact_id, value)}
                          label="Your approval decision"
                          compact
                        />
                      </div>
                    ) : humanApproval ? (
                      <p className="mt-2">
                        <ArtefactApprovalBadge decision={humanApproval} prefix="You" />
                      </p>
                    ) : null}
                  </div>
                  <div className="flex w-52 flex-shrink-0 flex-col items-end gap-2">
                    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                      {showAceRatings ? (
                        <>
                          {humanRag ? (
                            <SummaryRagBadge rag={humanRag} label="You" />
                          ) : (
                            <span className="text-xs text-rose-400">You: Not rated</span>
                          )}
                          <SummaryRagBadge
                            rag={aceRag}
                            label="Ace"
                            loading={aceLoading && !aceRag}
                          />
                          {humanRag && aceRag ? (
                            disagrees ? (
                              <FontAwesomeIcon icon={faCircleXmark} className="text-amber-400 text-xs" />
                            ) : (
                              <FontAwesomeIcon icon={faCircleCheck} className="text-emerald-400 text-xs" />
                            )
                          ) : null}
                        </>
                      ) : humanRag ? (
                        <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${ragOption(humanRag).text}`}>
                          <span className={`w-2 h-2 rounded-full ${ragOption(humanRag).dot}`} aria-hidden />
                          {ragOption(humanRag).label}
                        </span>
                      ) : (
                        <span className="text-sm text-rose-400">Not rated</span>
                      )}
                    </div>
                    {showAceRatings ? (
                      <div className="flex flex-col items-end gap-1">
                        {aceLoading && !aceApproval ? (
                          <span className="inline-flex items-center gap-1.5 text-xs text-indigo-300">
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[10px]" aria-hidden />
                            <span className="text-slate-400">Ace recommends:</span>
                            Rating…
                          </span>
                        ) : (
                          <ArtefactApprovalBadge decision={aceApproval} prefix="Ace recommends" />
                        )}
                        {humanApproval && aceApproval ? (
                          approvalDisagrees ? (
                            <FontAwesomeIcon icon={faCircleXmark} className="text-amber-400 text-xs" />
                          ) : (
                            <FontAwesomeIcon icon={faCircleCheck} className="text-emerald-400 text-xs" />
                          )
                        ) : null}
                      </div>
                    ) : null}
                    <div className="flex items-center gap-3">
                      {showAceRatings && onCompareArtefact ? (
                        <button
                          type="button"
                          onClick={() => onCompareArtefact(artefact.artifact_id)}
                          className="text-xs text-indigo-300 hover:text-indigo-200"
                        >
                          Compare
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => onEdit("artefacts")}
                        className="text-xs text-indigo-300 hover:text-indigo-200"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          : null}
      </div>

      {showAcePanel && onRunAce ? (
        <AceComparisonPanel
          comparison={aceComparison}
          loading={aceLoading}
          error={aceError}
          reviewId={reviewId}
          catalogueKey={catalogueKey}
          aceTrialRefreshToken={aceTrialRefreshToken}
          humanPhaseRatings={ratings}
          onRunAce={onRunAce}
          onRerunAce={onRerunAce}
          onRunFreshAce={onRunFreshAce}
          onSelectAceTrial={onSelectAceTrial}
          onPromoteAceTrial={onPromoteAceTrial}
          focusCheckId={focusCheckId}
        />
      ) : null}

      {showVersionHistory ? (
        <ReviewVersionHistory
          reviewId={reviewId}
          currentVersion={currentVersion}
          refreshToken={versionRefreshToken}
        />
      ) : null}
    </div>
  );
}
